import { useState, useEffect, useRef } from 'react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Button } from '@/components/ui/button';
import { PanelRightClose, PanelRightOpen, List, FileText, Sparkles } from 'lucide-react';

/**
 * ResizableWorkspaceLayout — Sprint 6 follow-up.
 *
 * The 3-pane workspace (Chapters list / Editor / Intelligence) used to be a
 * fixed grid (`lg:grid-cols-[260px_1fr_380px]`). On 13-14" laptop screens
 * (≈1280-1440px) that left only ~640px for the editor, which is cramped for
 * dissertation writing. This wrapper:
 *
 *   • introduces drag-to-resize handles between the three panes (powered by
 *     `react-resizable-panels`, ~5 KB gzipped),
 *   • persists the user's preferred sizes to localStorage via `autoSaveId`,
 *   • adds a one-click collapse button for the right Intelligence pane so
 *     PhDs can temporarily go to a 2-pane layout when they want maximum
 *     editor width,
 *   • falls back to a single-column stack below the Tailwind `lg` (1024px)
 *     breakpoint, matching prior mobile behaviour.
 *
 * No state related to the panes' internal logic lives here — it's a pure
 * layout primitive. The parent (`ThesisWorkspace.js`) is unaffected; it
 * just changes `<div className="grid …">` to
 * `<ResizableWorkspaceLayout left={…} center={…} right={…} />`.
 */
export default function ResizableWorkspaceLayout({
  left,
  center,
  right,
  /** PanelGroup autoSaveId — scope by project id so each thesis remembers
   * its own layout. Falls back to a stable global key. */
  storageKey = 'inkphd-workspace-layout',
  /** Initial sizes (percent). Sum should be 100. */
  defaultSizes = [22, 48, 30],
  /** Minimum widths in % so panes can never collapse to unusable widths. */
  minSizes = [14, 30, 18],
  /** Bump this counter to force the mobile switcher onto `focusPane`. */
  focusKey = 0,
  focusPane = 'editor',
}) {
  // Right-pane collapse state — distinct from the resizable size because we
  // want a one-click toggle that fully hides the pane, not just shrinks it.
  const [rightCollapsed, setRightCollapsed] = useState(false);
  // Mobile (Sprint 7) — show only ONE pane at a time on small screens, with
  // a 3-button switcher at the top. Stacking all three vertically was OK on
  // tablets but hostile on iPhones (≤ 430 px) — too much scrolling.
  const [mobilePane, setMobilePane] = useState('editor');
  const panelGroupRef = useRef(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(`${storageKey}__right_collapsed`);
      if (stored === '1') setRightCollapsed(true);
    } catch {
      // localStorage may be unavailable in private mode — ignore.
    }
  }, [storageKey]);

  useEffect(() => {
    if (focusKey) setMobilePane(focusPane);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const handleToggleRight = () => {
    const next = !rightCollapsed;
    setRightCollapsed(next);
    try {
      localStorage.setItem(`${storageKey}__right_collapsed`, next ? '1' : '0');
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" data-testid="chapters-layout">
      {/* Floating collapse toggle for the right pane — top-right of the layout */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggleRight}
        className="hidden lg:inline-flex absolute -top-1 right-0 h-7 w-7 text-slate-500 hover:text-blue-600 z-10"
        data-testid="workspace-toggle-intelligence-panel"
        title={rightCollapsed ? 'Show Intelligence Panel' : 'Hide Intelligence Panel'}
      >
        {rightCollapsed ? <PanelRightOpen className="h-4 w-4" /> : <PanelRightClose className="h-4 w-4" />}
      </Button>

      {/* Mobile / small screens (Sprint 7): tabbed single-pane navigation
          instead of vertical stack. Each pane renders full-width when active. */}
      <div className="lg:hidden">
        <div className="grid grid-cols-3 gap-1 mb-3 p-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
          data-testid="workspace-mobile-tabs">
          <button
            onClick={() => setMobilePane('chapters')}
            className={`flex items-center justify-center gap-1.5 py-2 rounded text-[12px] font-medium transition-colors ${
              mobilePane === 'chapters'
                ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            data-testid="mobile-tab-chapters">
            <List className="h-3.5 w-3.5" /> Chapters
          </button>
          <button
            onClick={() => setMobilePane('editor')}
            className={`flex items-center justify-center gap-1.5 py-2 rounded text-[12px] font-medium transition-colors ${
              mobilePane === 'editor'
                ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            data-testid="mobile-tab-editor">
            <FileText className="h-3.5 w-3.5" /> Editor
          </button>
          <button
            onClick={() => setMobilePane('intelligence')}
            className={`flex items-center justify-center gap-1.5 py-2 rounded text-[12px] font-medium transition-colors ${
              mobilePane === 'intelligence'
                ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            data-testid="mobile-tab-intelligence">
            <Sparkles className="h-3.5 w-3.5" /> AI
          </button>
        </div>
        <div className="min-h-[calc(100vh-16rem)]">
          {mobilePane === 'chapters' && <div data-testid="mobile-pane-chapters">{left}</div>}
          {mobilePane === 'editor' && <div data-testid="mobile-pane-editor">{center}</div>}
          {mobilePane === 'intelligence' && <div data-testid="mobile-pane-intelligence">{right}</div>}
        </div>
      </div>

      {/* Desktop: resizable 3-pane layout */}
      <div className="hidden lg:block h-[calc(100vh-12rem)] min-h-[600px]">
        <PanelGroup
          direction="horizontal"
          autoSaveId={storageKey}
          ref={panelGroupRef}
          className="h-full"
        >
          <Panel
            defaultSize={defaultSizes[0]}
            minSize={minSizes[0]}
            className="!overflow-hidden"
            data-testid="workspace-pane-chapters"
          >
            <div className="h-full overflow-y-auto pr-2">
              {left}
            </div>
          </Panel>

          <PanelResizeHandle
            className="w-1.5 mx-0.5 rounded-full bg-transparent hover:bg-blue-300/60 dark:hover:bg-blue-700/60 transition-colors data-[resize-handle-state=drag]:bg-blue-400 data-[resize-handle-state=drag]:dark:bg-blue-600"
            data-testid="workspace-resize-handle-left"
          />

          <Panel
            defaultSize={rightCollapsed ? 100 - defaultSizes[0] : defaultSizes[1]}
            minSize={minSizes[1]}
            className="!overflow-hidden"
            data-testid="workspace-pane-editor"
          >
            <div className="h-full overflow-y-auto px-2">
              {center}
            </div>
          </Panel>

          {!rightCollapsed && (
            <>
              <PanelResizeHandle
                className="w-1.5 mx-0.5 rounded-full bg-transparent hover:bg-blue-300/60 dark:hover:bg-blue-700/60 transition-colors data-[resize-handle-state=drag]:bg-blue-400 data-[resize-handle-state=drag]:dark:bg-blue-600"
                data-testid="workspace-resize-handle-right"
              />
              <Panel
                defaultSize={defaultSizes[2]}
                minSize={minSizes[2]}
                className="!overflow-hidden"
                data-testid="workspace-pane-intelligence"
              >
                <div className="h-full overflow-y-auto pl-2">
                  {right}
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>
    </div>
  );
}
